import React, { createContext, useContext, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "./LocalSess";
import { decryptSessionData, encryptSessionData } from "../utils/Encrypt";
import PropTypes from "prop-types";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [userData, setUserData] = useLocalStorage("smsCallUser", null);
  const navigate = useNavigate();

  // ---- Session derivation ----
  const session = useMemo(() => {
    if (!userData) return null;
    try {
      return decryptSessionData(userData)?.[0] ?? null;
    } catch {
      return null;
    }
  }, [userData]);

  // ---- Token validation ----
  const isSessionValid = useMemo(() => {
    if (!session?.token) return false;

    try {
      const payload = JSON.parse(atob(session.token.split(".")[1]));
      const now = Date.now() / 1000;
      return payload.exp > now;
    } catch {
      return false;
    }
  }, [session]);

  const isAuthenticated = !!session && isSessionValid;

  useEffect(() => {
    if (session && !isSessionValid) {
      setUserData(null);
    }
  }, [session, isSessionValid, setUserData]);

  // ---- Derived ----
  const role = session?.id_usertype ?? null;

  const isAdmin = role === 1 || role === 4 || role === 5;

  const channels = isAdmin ? ["ALL"] : session?.j_channels ?? [];

  // ---- Backward-compatible API ----

  const getSessionData = () => session;

  const isAdminRole = () => isAdmin;

  const getUserRole = () => role;

  const getUserChannel = () => channels;

  const login = (data, token) => {
    const updated = { ...data[0], token };
    setUserData(encryptSessionData([updated]));
    navigate("/home", { replace: true });
  };

  const logout = () => {
    setUserData(null);
    navigate("/login", { replace: true });
  };

  const logoutNoRedirect = () => {
    setUserData(null);
  };

  const updateUserData = (newData) => {
    if (!session) return;
    const updated = { ...session, ...newData };
    setUserData(encryptSessionData([updated]));
  };

  const value = useMemo(
    () => ({
      userData,
      session,
      isAuthenticated,
      role,
      isAdmin,
      channels,
      login,
      logout,
      logoutNoRedirect,
      updateUserData,
      getSessionData,
      isAdminRole,
      getUserRole,
      getUserChannel,
    }),
    [userData, session, isAuthenticated, role, isAdmin, channels],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export const useAuth = () => useContext(AuthContext);
